// HR API client. See api/src/routes/hr.ts and docs/reports-notes.md
// "HR & Employee Info" for the data-model rationale (notably: master `id`
// is the URL key, NOT `empId`, because the satellite tables FK to it).

export interface EmployeeSummary {
  id: number;
  empId: number;
  name: string;
  empType: number;
  empTypeLabel: string;
  salary: number;
  commPercent: number;
  morningComm: number;
  eveningComm: number;
  inTime: string;
  outTime: string;
  tea: number;
  leaves: number;
  leaveComm: string;
  mobile: string;
  active: boolean;
  discontinueDate: string;
  advance: { debit: number; credit: number; balance: number };
}

export interface EmployeeListTotals {
  total: number;
  active: number;
  inactive: number;
  advanceBalance: number;
}

export interface EmployeeListResponse {
  employees: EmployeeSummary[];
  totals: EmployeeListTotals;
}

export interface AttendanceDay  { date: string; entries: number; firstAt: string; lastAt: string }
export interface LeaveEntry     { id: number; date: string; halfDay: boolean }
export interface AdvanceEntry   { id: number; date: string; debit: number; credit: number; remarks: string; trantype: number }
export interface SalaryEntry    {
  id: number; year: number; month: number;
  basic: number; total: number;
  leaves: number; halfDays: number; absent: number;
  tea: number; brandCommission: number; pctCommission: number;
  holidayCount: number; holidayAmount: number;
  lateDeduction: number; leaveDeduction: number;
}

export interface EmployeeDetail {
  employee: Omit<EmployeeSummary, 'advance'>;
  attendance: AttendanceDay[];
  leaves: LeaveEntry[];
  advances: AdvanceEntry[];
  advanceTotal: { debit: number; credit: number; balance: number };
  salaries: SalaryEntry[];
}

function qs(params: Record<string, string | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') u.set(k, v);
  return u.toString();
}

export async function listEmployees(p: {
  q?: string;
  status?: 'active' | 'inactive' | 'all';
  store?: string | null;
}): Promise<EmployeeListResponse> {
  const r = await fetch(`/api/hr/employees?${qs({ q: p.q, status: p.status, store: p.store ?? undefined })}`, {
    credentials: 'include',
  });
  if (!r.ok) throw new Error(`employees list failed (${r.status})`);
  return r.json() as Promise<EmployeeListResponse>;
}

export interface AttendanceDailyPoint { date: string; present: number; punches: number }
export interface AttendanceDailyResponse { activeCount: number; days: AttendanceDailyPoint[] }

export async function getAttendanceDaily(p: {
  days?: number;
  store?: string | null;
}): Promise<AttendanceDailyResponse> {
  const r = await fetch(`/api/hr/attendance/daily?${qs({
    days: p.days ? String(p.days) : undefined,
    store: p.store ?? undefined,
  })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`attendance daily failed (${r.status})`);
  return r.json() as Promise<AttendanceDailyResponse>;
}

export async function getEmployee(p: { id: number; store?: string | null }): Promise<EmployeeDetail> {
  const r = await fetch(`/api/hr/employees/${p.id}?${qs({ store: p.store ?? undefined })}`, {
    credentials: 'include',
  });
  if (!r.ok) throw new Error(`employee detail failed (${r.status})`);
  return r.json() as Promise<EmployeeDetail>;
}
